<!--
  Add-route modal shared by the line map screens (`line_map#show` / `users#show`
  and `collabo_line_map#show`).

  The legacy pages opened this dialog with Bootstrap 4's jQuery plugin
  (`$('.modal').modal('show')`), which pulls in jQuery and the Bootstrap JS.
  This component replaces that plugin with Vue state while keeping the
  Bootstrap `modal` / `modal-dialog` / `modal-content` markup so the CSS still
  looks the same (only the JS is gone).

  The close behavior mirrors `lib/confirm.js`:
    - Esc closes the dialog,
    - a click on the backdrop / outside the dialog closes it,
    - the page scroll is locked while it is open,
    - focus moves into the dialog on open and is restored on close.
-->
<template>
  <div
    v-if="open"
    class="modal fade show d-block"
    tabindex="-1"
    role="dialog"
    aria-label="ルートの追加"
    aria-modal="true"
    @mousedown.self="close"
  >
    <div ref="dialog" class="modal-dialog" role="document">
      <div class="modal-content">
        <div class="modal-body">
          <div class="row">
            <div class="col-12">ルートのURLを入力</div>
            <div class="col-12 mb-2">
              <input
                ref="urlInput"
                id="new"
                class="form-control"
                type="text"
                placeholder="https://..."
              />
            </div>
          </div>
          <div class="row">
            <div class="col-12">対応サイト</div>
            <div class="col-12">
              <a href="https://latlonglab.yahoo.co.jp/route" target="_blank">ルートラボ(終了)</a>
            </div>
            <div class="col-12">
              <a href="https://www.bikemap.net" target="_blank">bikemap</a>
            </div>
            <div class="col-12">
              <a href="https://ridewithgps.com" target="_blank">Ride With GPS</a>
            </div>
          </div>
          <div class="row mb-2 mt-4">
            <div class="col-12">{{ fileLabel }}</div>
            <div class="col-12">
              <input
                ref="fileInput"
                id="new_gpx"
                class="form-control"
                type="file"
                multiple="multiple"
                accept=".gpx,.GPX,application/gpx+xml"
                @change="onFileChange"
              />
            </div>
            <div class="col-12">CtrlまたはShiftで複数ファイル選択可能 ← new!</div>
          </div>
          <div class="row">
            <div class="col-12">
              <button id="send" class="btn btn-danger" @click.prevent="send">追加</button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
  <!-- Bootstrap's backdrop is kept for its look; the click handler is on the
       `.modal` root above, which sits on top of it. -->
  <div v-if="open" class="modal-backdrop fade show"></div>
</template>

<script>
export default {
  name: 'AddRouteModal',

  props: {
    open: { type: Boolean, default: false },
    // The two screens differ only in this label (`line_map` adds "(ベータ)").
    fileLabel: { type: String, default: 'またはGPXファイルをアップロード' },
  },

  emits: ['close', 'send'],

  created() {
    // `FileList` values cannot be kept in Vue state (and cannot be written
    // back into an `<input type="file">` directly), so the selected files are
    // parked in a `DataTransfer` and reassigned when the input is re-created.
    // Stored on the instance (not in `data()`) to keep the File objects out of
    // Vue's reactivity.
    this.retainedFiles = null
    // Whether the keydown listener / scroll lock are currently installed.
    // `activate()` installs them synchronously so `deactivate()` is always able
    // to undo them, even when `open` flips back before `$nextTick` runs.
    this.activated = false
  },

  watch: {
    open(value) {
      if (value) {
        this.activate()
      } else {
        this.deactivate()
      }
    },
  },

  mounted() {
    if (this.open) this.activate()
  },

  beforeUnmount() {
    this.deactivate()
  },

  methods: {
    activate() {
      if (this.activated) return
      this.activated = true
      // The keydown listener and the body scroll lock do not depend on the
      // rendered DOM, so install them synchronously. `deactivate()` then always
      // has something symmetric to undo, so a fast `open` true -> false flip
      // (or an unmount right after opening) cannot leave them behind.
      this.previouslyFocused = document.activeElement
      this.previousOverflow = document.body.style.overflow
      document.body.style.overflow = 'hidden'
      document.addEventListener('keydown', this.onKeydown, true)

      // The dialog is rendered with `v-if`, so the refs only exist after the
      // next DOM update.
      this.$nextTick(() => {
        // `open` may already be false (or the component already deactivated)
        // if the modal was closed right after opening.
        if (!this.activated) return
        // The legacy `openAddModal` cleared `#new` every time it was opened,
        // but left the `#new_gpx` selection alone.
        if (this.$refs.urlInput) this.$refs.urlInput.value = ''
        if (this.$refs.fileInput && this.retainedFiles) {
          this.$refs.fileInput.files = this.retainedFiles.files
        }
        const target = this.$refs.urlInput
        if (target) {
          try {
            target.focus({ preventScroll: true })
          } catch (e) {
            target.focus()
          }
        }
      })
    },

    deactivate() {
      if (!this.activated) return
      this.activated = false
      document.removeEventListener('keydown', this.onKeydown, true)
      if (this.previousOverflow !== undefined) {
        document.body.style.overflow = this.previousOverflow
      }
      const previous = this.previouslyFocused
      this.previouslyFocused = null
      this.previousOverflow = undefined
      if (previous && typeof previous.focus === 'function' && document.contains(previous)) {
        try {
          previous.focus({ preventScroll: true })
        } catch (e) {
          previous.focus()
        }
      }
    },

    // Keep the selected files so they survive close/reopen, matching the
    // legacy Bootstrap modal (which kept the hidden input in the DOM).
    onFileChange(event) {
      const files = Array.from((event.target && event.target.files) || [])
      try {
        const transfer = new DataTransfer()
        files.forEach((file) => transfer.items.add(file))
        this.retainedFiles = transfer
      } catch (e) {
        // `DataTransfer` is not available in this browser: do not retain.
        this.retainedFiles = null
      }
    },

    close() {
      this.$emit('close')
    },

    send() {
      const url = this.$refs.urlInput ? this.$refs.urlInput.value : ''
      const files = this.$refs.fileInput ? this.$refs.fileInput.files : []
      this.$emit('send', { url, files })
      // The legacy `onClickSend` cleared `#new_gpx` after sending, so the next
      // time the modal opens it starts with no file selected.
      this.retainedFiles = null
      if (this.$refs.fileInput) this.$refs.fileInput.value = ''
    },

    onKeydown(event) {
      if (event.key === 'Escape') {
        event.preventDefault()
        this.close()
        return
      }
      if (event.key !== 'Tab') return

      // Keep focus inside the dialog while it is open.
      const dialog = this.$refs.dialog
      if (!dialog) return
      const focusables = Array.from(
        dialog.querySelectorAll(
          'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
        )
      ).filter((el) => el.offsetParent !== null || el === document.activeElement)
      if (focusables.length === 0) return
      const index = focusables.indexOf(document.activeElement)
      if (event.shiftKey) {
        if (index <= 0) {
          event.preventDefault()
          focusables[focusables.length - 1].focus()
        }
      } else if (index === -1 || index === focusables.length - 1) {
        event.preventDefault()
        focusables[0].focus()
      }
    },
  },
}
</script>
